import React, { useEffect } from 'react'
import { Typography, makeStyles, CircularProgress, Container } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import pollingSlice, {
  getActivePollWithinSession,
  getActivePollWithinTalkEvent,
  getActiveQuestionWithinPoll,
  getAnswersOfAttendeeForPoll,
  getPollById
} from 'store/polling'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import PollQuestionAttempt from 'views/admin/polling/public/PollQuestionAttempt'
import { POLLING_END_USER_MSGS, PollStatus } from 'constants/index'

const LEGACY_POLLING_HOST = 'meet-csymple.web.app'
const NEW_POLLING_URL = 'https://meetsymple.web.app/polling/'

const PoolForm = () => {
  const history = useHistory()
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const attendeeId = queryParams.get('aid')
  const pollId = queryParams.get('pollId')
  const eventId = queryParams.get('eventId')
  const sessionId = queryParams.get('sessionId')
  const { inProgress, poll, question } = useSelector((state) => state.polling)
  const isLegacyHost = window.location.hostname === LEGACY_POLLING_HOST

  useEffect(() => {
    if (isLegacyHost) {
      window.location.replace(`${NEW_POLLING_URL}${location.search}${location.hash}`)
    }
  }, [isLegacyHost])

  useEffect(() => {
    if (eventId && sessionId && poll?.id && poll?.status === PollStatus.STARTED) {
      history.push(`/polling?pollId=${poll?.id}&aid=${attendeeId}`)
    }
  }, [eventId, sessionId, poll?.id, poll?.status])

  useEffect(async () => {
    if (isLegacyHost) return
    dispatch(pollingSlice.actions.setAnsweredBy(attendeeId))
    dispatch(pollingSlice.actions.setProgress(true))
    if (pollId) {
      const unsub = getPollById(pollId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsub && unsub()
    } else if (eventId && sessionId) {
      const unsubActivePoll = getActivePollWithinSession(eventId, sessionId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsubActivePoll && unsubActivePoll()
    } else if (eventId && !sessionId) {
      const unsubActivePoll = getActivePollWithinTalkEvent(eventId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsubActivePoll && unsubActivePoll()
    }
  }, [dispatch, pollId])

  useEffect(() => {
    if (poll?.id && (poll?.status === PollStatus.STARTED || poll?.status === PollStatus.RESUMED)) {
      const unsubQuestion = getActiveQuestionWithinPoll(poll?.id, (data) => {
        dispatch(pollingSlice.actions.setQuestion(data))
      })
      return () => unsubQuestion && unsubQuestion()
    }
  }, [dispatch, poll?.id, poll?.status])

  useEffect(() => {
    if (
      question?.id &&
      attendeeId &&
      (poll?.status === PollStatus.STARTED || poll?.status === PollStatus.RESUMED)
    ) {
      const unsubAnswers = getAnswersOfAttendeeForPoll(poll?.id, attendeeId, (data) => {
        dispatch(pollingSlice.actions.setAnswers(data))
      })
      return () => unsubAnswers && unsubAnswers()
    }
  }, [dispatch, poll?.id, poll?.status, question?.id])

  if (isLegacyHost || inProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )
  } else if (!poll || (!poll?.isActive && poll?.status === PollStatus.CREATED)) {
    return (
      <InfoBox
        header={POLLING_END_USER_MSGS.inActive.header}
        subHeader={POLLING_END_USER_MSGS.inActive.subText}
      />
    )
  } else if (poll?.status === PollStatus.CREATED) {
    return (
      <InfoBox
        header={POLLING_END_USER_MSGS.toBegin.header}
        subHeader={POLLING_END_USER_MSGS.toBegin.subText}
      />
    )
  } else if (poll?.status === PollStatus.PAUSED) {
    return (
      <InfoBox
        header={POLLING_END_USER_MSGS.toResume.header}
        subHeader={POLLING_END_USER_MSGS.toResume.subText}
      />
    )
  } else if (!poll?.isActive && poll?.status === PollStatus.CLOSED) {
    return (
      <InfoBox
        header={POLLING_END_USER_MSGS.closed.header}
        subHeader={POLLING_END_USER_MSGS.closed.subText}
      />
    )
  } else {
    return (
      <Container className={classes.container}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          <PollQuestionAttempt question={question} />
        </Box>
      </Container>
    )
  }
}

export default PoolForm

export const InfoBox = ({ header, subHeader }) => {
  const classes = useStyles()
  return (
    <Box className={classes.msgBox}>
      <Typography className={classes.waitingHeader}>{header}</Typography>
      <Typography className={classes.waitingSubHeader}>{subHeader}</Typography>
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.5rem 1rem'
    },
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  msgBox: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px'
  },
  waitingHeader: {
    fontWeight: 'bold',
    color: '#101010'
  },
  waitingSubHeader: {
    fontSize: '14px',
    marginTop: '10px'
  }
}))
